import { useState, useEffect } from 'react';
// Helper functions (kept outside the hook because they don't depend on props or state)
const resolveRef = (ref, root) => {
    const path = ref.split('/');
    if (path[0] !== '#')
        return null;
    let current = root;
    for (let i = 1; i < path.length; i++) {
        if (current === null || typeof current !== 'object' || !current.hasOwnProperty(path[i]))
            return null;
        current = current[path[i]];
    }
    return current;
};
const isProvideOrLiteral = (value) => {
    return typeof value === 'object' && value !== null && ('##provide' in value || '##literal' in value);
};
const isFieldActive = (value) => {
    // A field is considered "active" if it has a meaningful value
    return !(value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'object' && !isProvideOrLiteral(value) && Object.keys(value).length === 0) ||
        (Array.isArray(value) && value.length === 0));
};
const validateRequiredParams = (params, schema, rootSchema, isTopLevel = true) => {
    if (typeof schema !== 'object' || schema === null)
        return true;
    // Handle conditional validation based on JSON Schema if/then/else
    if (schema.if && schema.then && params) {
        // Only proceed if if/then/else are schema objects, not booleans
        const ifSchema = typeof schema.if === 'object' ? schema.if : null;
        const thenSchema = typeof schema.then === 'object' ? schema.then : null;
        const elseSchema = typeof schema.else === 'object' ? schema.else : null;
        if (ifSchema) {
            const conditionMet = validateRequiredParams(params, ifSchema, rootSchema, false);
            if (conditionMet && thenSchema) {
                const thenValid = validateRequiredParams(params, thenSchema, rootSchema, false);
                if (!thenValid)
                    return false;
            }
            else if (!conditionMet && elseSchema) {
                const elseValid = validateRequiredParams(params, elseSchema, rootSchema, false);
                if (!elseValid)
                    return false;
            }
        }
    }
    // Only validate required fields if:
    // 1. We're at the top level (always validate top-level required fields), OR
    // 2. The current object/context is "active" (has some meaningful content)
    const shouldValidateRequired = isTopLevel || isFieldActive(params);
    if (schema.required && shouldValidateRequired) {
        for (const field of schema.required) {
            const value = params === null || params === void 0 ? void 0 : params[field];
            if (!isFieldActive(value)) {
                return false;
            }
        }
    }
    // Recursively validate properties, but only if they are active
    if (schema.properties) {
        for (const key in schema.properties) {
            const propSchema = schema.properties[key];
            const propValue = params === null || params === void 0 ? void 0 : params[key];
            if (typeof propSchema === 'object' &&
                isFieldActive(propValue) && // Only validate if the property has a value
                !validateRequiredParams(propValue, propSchema, rootSchema, false)) {
                return false;
            }
        }
    }
    if (schema.oneOf) {
        // For oneOf, we need to check if at least one schema validates successfully
        // This is important for union types where only one branch should be validated
        return schema.oneOf.some((subSchema) => {
            if (typeof subSchema !== 'object' || subSchema === null)
                return false;
            const resolved = subSchema.$ref ? resolveRef(subSchema.$ref, rootSchema) : subSchema;
            return resolved ? validateRequiredParams(params, resolved, rootSchema, false) : false;
        });
    }
    if (schema.allOf) {
        // For allOf, all schemas must validate
        return schema.allOf.every((subSchema) => {
            if (typeof subSchema !== 'object' || subSchema === null)
                return false;
            const resolved = subSchema.$ref ? resolveRef(subSchema.$ref, rootSchema) : subSchema;
            return resolved ? validateRequiredParams(params, resolved, rootSchema, false) : false;
        });
    }
    return true;
};
export const useScenarioItemValidator = ({ isModalVisible, isMainFormValid, modalData, selectedDef, patchedSchema, }) => {
    const [isOkDisabled, setIsOkDisabled] = useState(true);
    useEffect(() => {
        if (!isModalVisible)
            return;
        if (!isMainFormValid) {
            setIsOkDisabled(true);
            return;
        }
        const schemaToValidate = patchedSchema || (selectedDef === null || selectedDef === void 0 ? void 0 : selectedDef.paramsSchema);
        if (!schemaToValidate || Object.keys(schemaToValidate).length === 0) {
            setIsOkDisabled(false);
            return;
        }
        const isValid = validateRequiredParams(modalData === null || modalData === void 0 ? void 0 : modalData.params, schemaToValidate, schemaToValidate, true);
        setIsOkDisabled(!isValid);
    }, [isModalVisible, modalData, selectedDef, isMainFormValid, patchedSchema]);
    return isOkDisabled;
};
